import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FormAlert,
  FormCard,
  FormDialog,
  FormField,
  FormGrid,
  SettingsButton,
  TableKit,
} from "@NP-Webber/crm-kit";
import {
  financeApi,
  formatShekels,
  type Subscription,
} from "../../api/financeApi";
import { financeStructureApi, type FinanceCategory } from "../../api/financeStructureApi";
import { customersApi, type CustomerContact } from "../../api/customersApi";
import {
  type SubscriptionWithPeriod,
} from "../../api/nedarimApi";
import { toClientApiError } from "../../api/httpClient";
import { useEntityLabels } from "../../i18n/EntityLabelsProvider";
import { LookupSelect } from "../../ui/LookupSelect";
import { useLookupOptions } from "../../ui/useLookupOptions";
import { SubscriptionCard } from "../cashLedger/SubscriptionCard";

export type CustomerSubscriptionsPanelProps = {
  customerId: string;
  customerName: string;
  archived: boolean;
};

const STATUS_OPTIONS = [
  { value: "active", label: "פעיל" },
  { value: "paused", label: "מושהה" },
  { value: "draft", label: "טיוטה" },
  { value: "cancelled", label: "בוטל" },
  { value: "expired", label: "פג" },
];

function shekelsToAgorot(value: string): number | null {
  const normalized = value.replace(",", ".").trim();
  if (!normalized) return null;
  const num = Number(normalized);
  if (!Number.isFinite(num) || num <= 0) return null;
  return Math.round(num * 100);
}

function statusLabel(status: string): string {
  return STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("he-IL");
}

function attrNumber(
  sub: Subscription,
  fieldKey: string,
): number | null {
  const attr = sub.attributes?.find((a) => a.fieldKey === fieldKey);
  if (!attr || typeof attr.value !== "number" || !Number.isFinite(attr.value)) {
    return null;
  }
  return attr.value;
}

function toPeriodSubscription(sub: Subscription): SubscriptionWithPeriod {
  const now = new Date();
  const billingPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  return {
    id: sub.id,
    customerId: sub.customerId,
    categoryId: sub.categoryId,
    title: sub.title,
    status: sub.status,
    amountAgorot: sub.amountAgorot,
    currency: sub.currency,
    paymentArrangement: sub.paymentArrangement,
    nedarimKevaId: sub.nedarimKevaId ?? null,
    nedarimChannel: sub.nedarimChannel ?? null,
    lastChargeStatusKey: sub.lastChargeStatusKey ?? null,
    lastChargeOn: sub.lastChargeOn ?? null,
    billingPeriod,
    periodChargeStatus:
      sub.lastChargeStatusKey === "paid"
        ? "paid"
        : sub.lastChargeStatusKey === "failed"
          ? "failed"
          : sub.nedarimKevaId
            ? "unpaid"
            : "none",
    periodChargeOccurredOn: sub.lastChargeOn ?? null,
    startsOn: sub.startsOn,
    endsOn: sub.endsOn,
    createdAt: sub.createdAt,
    updatedAt: sub.updatedAt,
    attributes: sub.attributes,
  };
}

type TableRow = Record<string, unknown> & {
  id: string;
  categoryName: string;
  startsOn: string | null;
  endsOn: string | null;
  amountAgorot: number;
  dueAgorot: number | null;
  paidAgorot: number | null;
  balanceAgorot: number | null;
  paymentArrangementLabel: string;
  status: string;
  contactName: string;
  _sub: Subscription;
};

export function CustomerSubscriptionsPanel({
  customerId,
  customerName,
  archived,
}: CustomerSubscriptionsPanelProps) {
  const labels = useEntityLabels();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [categories, setCategories] = useState<FinanceCategory[]>([]);
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState<Subscription | null>(null);

  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [contactId, setContactId] = useState("");
  const [paymentArrangement, setPaymentArrangement] = useState("");
  const [status, setStatus] = useState("active");

  const { options: paymentArrangementOptions } = useLookupOptions(
    "payment_arrangements",
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const [subs, cats, contactRows] = await Promise.all([
        financeApi.listSubscriptions(customerId),
        financeStructureApi
          .listCustomerCategories(customerId)
          .catch(() => [] as FinanceCategory[]),
        customersApi.listContacts(customerId).catch(() => [] as CustomerContact[]),
      ]);
      setSubscriptions(subs);
      setCategories(cats);
      setContacts(contactRows);
    } catch (err) {
      setError(toClientApiError(err).message);
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const categoryName = useCallback(
    (id: string | null) =>
      id ? categories.find((c) => c.id === id)?.name ?? "—" : "—",
    [categories],
  );
  const contactName = useCallback(
    (id: string | null) =>
      id ? contacts.find((c) => c.id === id)?.displayName ?? "—" : "—",
    [contacts],
  );

  const paymentArrangementLabel = useCallback(
    (key: string | null) => {
      if (!key) return "—";
      return (
        paymentArrangementOptions.find((o) => o.key === key || o.labelHe === key)
          ?.labelHe ?? key
      );
    },
    [paymentArrangementOptions],
  );

  const tableRows: TableRow[] = useMemo(
    () =>
      subscriptions.map((sub) => {
        const dueAgorot = attrNumber(sub, "dueToDateAgorot");
        const paidAgorot = attrNumber(sub, "paidToDateAgorot");
        const balanceAgorot =
          dueAgorot != null && paidAgorot != null
            ? Math.max(0, dueAgorot - paidAgorot)
            : dueAgorot != null && paidAgorot == null
              ? dueAgorot
              : null;
        return {
          id: sub.id,
          categoryName: categoryName(sub.categoryId),
          startsOn: sub.startsOn,
          endsOn: sub.endsOn,
          amountAgorot: sub.amountAgorot,
          dueAgorot,
          paidAgorot,
          balanceAgorot,
          paymentArrangementLabel: paymentArrangementLabel(sub.paymentArrangement),
          status: sub.status,
          contactName: contactName(sub.contactId),
          _sub: sub,
        };
      }),
    [subscriptions, categoryName, contactName, paymentArrangementLabel],
  );

  const columns = useMemo(
    () => [
      {
        key: "categoryName",
        title: labels.project,
        sortable: true,
        filterable: true,
        minWidth: 120,
      },
      {
        key: "startsOn",
        title: "תחילה",
        sortable: true,
        minWidth: 100,
        render: (value: unknown) => formatDate(value == null ? null : String(value)),
      },
      {
        key: "endsOn",
        title: "סיום",
        sortable: true,
        minWidth: 100,
        render: (value: unknown) => formatDate(value == null ? null : String(value)),
      },
      {
        key: "amountAgorot",
        title: "חודשי",
        sortable: true,
        minWidth: 90,
        render: (value: unknown) => `${formatShekels(Number(value ?? 0))} ₪`,
      },
      {
        key: "dueAgorot",
        title: "לתשלום",
        sortable: true,
        minWidth: 100,
        render: (value: unknown) =>
          value == null ? "—" : `${formatShekels(Number(value))} ₪`,
      },
      {
        key: "paidAgorot",
        title: "שולם",
        sortable: true,
        minWidth: 100,
        render: (value: unknown) =>
          value == null ? (
            "—"
          ) : (
            <span style={{ color: "var(--ck-success)" }}>
              {formatShekels(Number(value))} ₪
            </span>
          ),
      },
      {
        key: "balanceAgorot",
        title: "יתרה",
        sortable: true,
        minWidth: 100,
        render: (value: unknown) => {
          if (value == null) return "—";
          const n = Number(value);
          return (
            <span
              style={{
                fontWeight: 600,
                color:
                  n > 0
                    ? "var(--ck-danger)"
                    : "var(--ck-success)",
              }}
            >
              {formatShekels(n)} ₪
            </span>
          );
        },
      },
      {
        key: "paymentArrangementLabel",
        title: "אופן תשלום",
        filterable: true,
        minWidth: 110,
      },
      {
        key: "status",
        title: "סטטוס",
        sortable: true,
        filterable: true,
        minWidth: 90,
        render: (value: unknown) => statusLabel(String(value ?? "")),
      },
      {
        key: "contactName",
        title: "איש קשר",
        filterable: true,
        minWidth: 120,
      },
    ],
    [labels.project],
  );

  const categoryOptions = [
    { value: "", label: "ללא קטגוריה" },
    ...categories.map((c) => ({ value: c.id, label: c.name })),
  ];
  const contactOptions = [
    { value: "", label: "ללא איש קשר" },
    ...contacts.map((c) => ({ value: c.id, label: c.displayName })),
  ];

  const resetCreateForm = () => {
    setTitle("");
    setAmount("");
    setCategoryId("");
    setContactId("");
    setPaymentArrangement("");
    setStatus("active");
  };

  const handleCreate = async () => {
    const amountAgorot = shekelsToAgorot(amount);
    if (amountAgorot == null) {
      setError("סכום מנוי לא תקין");
      return;
    }
    if (!title.trim()) {
      setError("כותרת מנוי חובה");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await financeApi.createSubscription({
        customerId,
        title: title.trim(),
        amountAgorot,
        categoryId: categoryId || null,
        contactId: contactId || null,
        paymentArrangement: paymentArrangement.trim() || null,
        status: status as Subscription["status"],
      });
      resetCreateForm();
      setCreateOpen(false);
      await load();
    } catch (err) {
      setError(toClientApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="related-panel related-panel--subscriptions">
      {error && !createOpen ? <FormAlert severity="error">{error}</FormAlert> : null}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 12,
          gap: 8,
        }}
      >
        {!archived ? (
          <SettingsButton
            type="button"
            variant="primarySettings"
            size="sm"
            onClick={() => {
              setError(null);
              setCreateOpen(true);
            }}
          >
            מנוי חדש
          </SettingsButton>
        ) : null}
      </div>

      {tableRows.length === 0 ? (
        <p className="related-panel__empty">אין מנויים עדיין.</p>
      ) : (
        <div className="page-section__table-surface">
          <TableKit
            columns={columns}
            data={tableRows as unknown as Array<Record<string, unknown>>}
            clientSideMode
            emptyMessage="אין מנויים עדיין."
            showFilters
            showColumnPicker
            rowClickMode="single"
            onRowClick={(row) => {
              const typed = row as TableRow;
              if (typed._sub) {
                setSelectedSub(typed._sub);
              }
              return true;
            }}
            onOpenCard={(row) => {
              const typed = row as TableRow;
              if (typed._sub) {
                setSelectedSub(typed._sub);
              }
              return true;
            }}
          />
        </div>
      )}

      <FormDialog
        open={createOpen}
        onClose={() => {
          if (!busy) {
            setCreateOpen(false);
            setError(null);
          }
        }}
        size="md"
      >
        <FormCard
          title="מנוי חדש"
          error={error}
          onSave={() => {
            void handleCreate();
          }}
          onCancel={() => {
            setCreateOpen(false);
            setError(null);
          }}
          saveLabel="יצירת מנוי"
          cancelLabel="ביטול"
          saving={busy}
          saveDisabled={busy || !title.trim() || !amount.trim()}
        >
          <FormGrid cols={2}>
            <FormField
              label="כותרת מנוי"
              value={title}
              onChange={setTitle}
              disabled={busy}
              required
            />
            <FormField
              label="סכום (₪)"
              value={amount}
              onChange={setAmount}
              disabled={busy}
              required
            />
            <FormField
              label={labels.project}
              type="select"
              value={categoryId}
              onChange={(next) => {
                setCategoryId(next);
                const cat = categories.find((c) => c.id === next);
                if (cat?.defaultSubscriptionAmountAgorot != null) {
                  setAmount(String(cat.defaultSubscriptionAmountAgorot / 100));
                }
              }}
              disabled={busy}
              options={categoryOptions}
            />
            <FormField
              label="איש קשר"
              type="select"
              value={contactId}
              onChange={setContactId}
              disabled={busy}
              options={contactOptions}
            />
            <LookupSelect
              label="צורת תשלום"
              variant="fixed"
              value={paymentArrangement}
              onChange={setPaymentArrangement}
              options={paymentArrangementOptions}
              disabled={busy}
            />
            <FormField
              label="סטטוס"
              type="select"
              value={status}
              onChange={setStatus}
              disabled={busy}
              options={STATUS_OPTIONS}
            />
          </FormGrid>
        </FormCard>
      </FormDialog>

      {selectedSub ? (
        <SubscriptionCard
          subscription={toPeriodSubscription(selectedSub)}
          customerName={customerName}
          categoryName={categoryName(selectedSub.categoryId)}
          onClose={() => setSelectedSub(null)}
        />
      ) : null}
    </div>
  );
}
